import { useMemo } from "react";
import { Bell, CaretRight, Check } from "@phosphor-icons/react";
import NotificationCard from "./NotificationCard";
import { BUCKET_LABEL, bucketOf } from "./_shared";

function SectionHeader({ label, count }) {
  return (
    <div className="flex items-center gap-2 px-2">
      <span className="text-[11px] font-extrabold tracking-wide text-[#5A6470]">{label}</span>
      <span className="text-[10.5px] text-[#8A8A8A] font-bold">· {count}</span>
      <span aria-hidden="true" className="flex-1 h-px bg-[#0A192F]/10" />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-2 mt-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-[92px] rounded-[28px] bg-white/45 backdrop-blur-md border border-white/55 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-16 px-6 text-center">
      <div className="mx-auto mb-5 w-20 h-20 rounded-full bg-white/55 backdrop-blur-md border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)] flex items-center justify-center">
        <Bell size={32} weight="duotone" className="text-[#0A192F]" />
      </div>
      <h3 className="text-[18px] text-[#0A192F] font-extrabold">صندوقك صامت</h3>
      <p className="mt-2 text-[12.5px] text-[#5A6470] leading-relaxed max-w-[280px] mx-auto">
        سنُنبّهك هنا فور وصول رسالة من موكّل، تذكير جلسة قادمة،
        أو قرار تمييز جديد يَخصّ ملفاتك.
      </p>
    </div>
  );
}

// Full-screen sheet — mirrors MobileLanding's palette exactly. Grouped
// by day-bucket (today / yesterday / week / older) with a sticky header
// showing the unread badge + "mark all" affordance.
export default function NotificationSheet({
  items, unread, loading, onClose, onReadAll, onRead, onDelete,
}) {
  const groups = useMemo(() => {
    const out = { today: [], yesterday: [], week: [], older: [] };
    for (const n of items) out[bucketOf(n.created_at)].push(n);
    return out;
  }, [items]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch lexiq-ntf-anim"
      data-testid="notification-sheet"
      onClick={onClose}
      style={{ direction: "rtl" }}
    >
      <div className="absolute inset-0 bg-[#0A192F]/30 backdrop-blur-[2px]" />
      <aside
        className="relative ml-auto w-full sm:w-[480px] flex flex-col h-full lexiq-ntf-panel font-tajawal"
        onClick={(e) => e.stopPropagation()}
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          background:
            "linear-gradient(180deg,#E4E4E4 0%, #E4E4E4 calc(env(safe-area-inset-top, 0px) + 60px), #E0E0E0 40%, #D0D0D0 75%, #C7C7C7 100%)",
        }}
      >
        <div className="px-4 pt-3 pb-3">
          <div className="flex items-center gap-3 p-3 pr-4 rounded-[28px] bg-white/55 backdrop-blur-md border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)]">
            <button
              type="button" data-testid="notif-sheet-close" onClick={onClose} aria-label="رجوع"
              className="w-11 h-11 rounded-full bg-white/85 backdrop-blur-sm border border-white/80 hover:bg-white flex items-center justify-center active:scale-95 transition"
            >
              <CaretRight size={20} weight="bold" className="text-[#0A192F]" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[20px] leading-none text-[#0A192F] font-extrabold">الإشعارات</h2>
                {unread > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-extrabold text-[#0A192F]"
                    style={{ background: "linear-gradient(135deg, #F4D464 0%, #D4AF37 60%, #A88425 100%)" }}
                  >
                    {unread}
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11.5px] text-[#5A6470] leading-snug">
                {unread > 0
                  ? `${unread} ${unread === 1 ? "إشعار غير مقروء" : "إشعارات غير مقروءة"}`
                  : "كل التحديثات أمامك"}
              </p>
            </div>
            {unread > 0 && (
              <button
                type="button" data-testid="mark-all-read-btn" onClick={onReadAll}
                className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-extrabold text-[#0A192F] px-3 py-2 rounded-full bg-white/85 backdrop-blur-sm border border-white/80 hover:bg-white transition active:scale-95"
                aria-label="تعليم الكل كمقروءة"
              >
                <Check size={12} weight="bold" />
                <span>الكل</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {loading && items.length === 0 ? <LoadingState />
            : items.length === 0 ? <EmptyState />
            : (
              <div className="space-y-5">
                {["today", "yesterday", "week", "older"].map((b) =>
                  groups[b].length > 0 ? (
                    <section key={b}>
                      <SectionHeader label={BUCKET_LABEL[b]} count={groups[b].length} />
                      <div className="mt-2 space-y-2">
                        {groups[b].map((n) => (
                          <NotificationCard key={n.id} n={n} onRead={onRead} onDelete={onDelete} />
                        ))}
                      </div>
                    </section>
                  ) : null,
                )}
              </div>
            )}
        </div>
      </aside>

      <style>{`
        .lexiq-ntf-anim   { animation: lexiqNtfFade 240ms ease-out; }
        .lexiq-ntf-panel  { animation: lexiqNtfSlide 360ms cubic-bezier(.22,1,.36,1); }
        @keyframes lexiqNtfFade  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes lexiqNtfSlide {
          from { transform: translateX(-14%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @media (max-width: 640px) {
          .lexiq-ntf-panel { animation-name: lexiqNtfUp; animation-duration: 400ms; }
          @keyframes lexiqNtfUp {
            from { transform: translateY(10%); opacity: 0; }
            to   { transform: translateY(0);   opacity: 1; }
          }
        }
      `}</style>
    </div>
  );
}
